import { ReactElement, useState } from "react";
import { IDepartmentSchema, ITask } from "../types";
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField
} from "@mui/material";
import Select from "@mui/material/Select";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import { useFetch } from "../../../../system/useFetch";

const DateFormat = "yyyy-MM-dd";

interface IProps {
  data: ITask;
  setData: (task: ITask) => void;
  isManager: boolean;
}

const TaskControl = ({ data, setData, isManager }: IProps): ReactElement => {
  const [startDate, setStartDate] = useState(data.startDate ? DateTime.fromFormat(data.startDate, DateFormat) : null);
  const [dueDate, setDueDate] = useState(data.dueDate ? DateTime.fromFormat(data.dueDate, DateFormat) : null);
  const [selectedDepartment, setSelectedDepartment] = useState<IDepartmentSchema | null>(null);
  const { data: departments } = useFetch<IDepartmentSchema[]>(
    {
      module: "task",
      query: "getDepartments",
      payload: {
        divisionID: data.division.divisionID
      }
    });

  const onSelectedDepartment = (departmentID: number) => {
    const department = departmentID !== -1 && departments ? departments.find(x => x.departmentID === departmentID) ?? null: null;

    if(department) {
      setData({ ...data, department: {
        departmentID: department.departmentID,
        code: department.code,
        name: department.name,
        color: department.color
      }
      });
    } else {
      setData({ ...data, department: null });
    }
    setSelectedDepartment(department);
  };

  const onChange = (field: keyof ITask, value: any) => {
    if (field === "assigned") {
      if (value === -1) {
        setData({ ...data, assigned: null });
      } else {
        const assigned = selectedDepartment?.users.find(x => x.userID === value) ?? null;
        setData({ ...data, assigned });
      }
    } else {
      setData({ ...data, [field]: value });
    }
  };

  const handleStartDateChange = (date: any) => {
    if (date && date.isValid) {
      setStartDate(date);

      setData({ ...data, startDate: date.toFormat(DateFormat) });
    }
  };

  const handleDueDateChange = (date: any) => {
    if (date && date.isValid) {
      setDueDate(date);
      setData({ ...data, dueDate: date.toFormat(DateFormat) });
    }
  };

  return (
    <div className="display-flex-column p-1 m-1 gap-3">

      <TextField
        sx={{ width: 224 }}
        variant="standard"
        placeholder={"Reference"}
        label={"Reference"}
        value={data.reference}
        disabled={data.approvedDate !== null}
        onChange={(event) => onChange("reference", event.target.value)}/>

      <div className="display-flex-row flex-nowrap">
        <FormControl style={{ width: 260, marginRight: 6 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            variant="standard"
            value={data.controlPriority}
            displayEmpty={true}
            disabled={data.approvedDate !== null || !isManager && data.taskID !== undefined}
            onChange={(event) => onChange("controlPriority", event.target.value as number)}>
            <MenuItem value={0}>Low</MenuItem>
            <MenuItem value={1}>Medium</MenuItem>
            <MenuItem value={2}>Important</MenuItem>
            <MenuItem value={3}>Urgent</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{ width: 260, marginLeft: 6 }}>
          <InputLabel>Progress</InputLabel>
          <Select
            variant="standard"
            value={data.progress}
            displayEmpty={true}
            disabled={data.approvedDate !== null || !isManager}
            onChange={(event) => onChange("progress", event.target.value as number)}>
            <MenuItem value={0}>Pending</MenuItem>
            <MenuItem value={1}>In Progress</MenuItem>
            <MenuItem value={2}>On Hold</MenuItem>
            <MenuItem value={3}>Cancelled</MenuItem>
            <MenuItem value={4}>Completed</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div className="display-flex-row flex-nowrap">
        <FormControl style={{ width: 260, marginRight: 6 }}>
          <InputLabel>Department</InputLabel>
          <Select
            variant="standard"
            value={selectedDepartment ? selectedDepartment.departmentID : -1}
            displayEmpty={true}
            disabled={data.approvedDate !== null || !isManager}
            onChange={(event) => onSelectedDepartment(event.target.value)}>
            <MenuItem value={-1}>None</MenuItem>
            {departments && departments.map(a => {
              return (
                <MenuItem key={`task-area-${a.departmentID}`} value={a.departmentID}>{a.name}</MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl style={{ width: 260, marginLeft: 6 }}>
          <InputLabel>Assign</InputLabel>
          <Select
            variant="standard"
            value={data.assigned ? data.assigned.userID : -1}
            displayEmpty={true}
            disabled={data.approvedDate !== null || !isManager}
            onChange={(event) => onChange("assigned", event.target.value)}>
            <MenuItem value={-1}>None</MenuItem>
            {selectedDepartment && selectedDepartment.users.map(u => {
              return (
                <MenuItem key={`task-zone-${u.userID}`} value={u.userID}>{u.userName}</MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>

      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <div className="display-flex-row flex-nowrap">
          <DatePicker
            label="Start Date"
            value={startDate}
            disabled={data.approvedDate !== null || !isManager}
            onChange={handleStartDateChange} />

          <DatePicker
            label="Due Date"
            value={dueDate}
            disabled={data.approvedDate !== null || !isManager}
            onChange={handleDueDateChange} />
        </div>
      </LocalizationProvider>

    </div>
  );
};

export default TaskControl;
